import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyLogin = useCallback(async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success) {
        setAdmin(response.data);
        setIsAuthenticated(true);
        // Store Base64-encoded auth token instead of raw credentials
        const token = btoa(`${username}:${password}`);
        sessionStorage.setItem('caf-admin-token', token);
      }
    } catch (error) {
      sessionStorage.removeItem('caf-admin-token');
      setIsAuthenticated(false);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('caf-admin-token');
    if (token) {
      try {
        const decoded = atob(token);
        const [username, password] = decoded.split(':');
        verifyLogin(username, password);
      } catch {
        sessionStorage.removeItem('caf-admin-token');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [verifyLogin]);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success) {
        setAdmin(response.data);
        setIsAuthenticated(true);
        const token = btoa(`${username}:${password}`);
        sessionStorage.setItem('caf-admin-token', token);
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('caf-admin-token');
    setIsAuthenticated(false);
    setAdmin(null);
  }, []);

  const getAuthHeader = useCallback(() => {
    const token = sessionStorage.getItem('caf-admin-token');
    if (token) {
      const decoded = atob(token);
      const [username, password] = decoded.split(':');
      return {
        auth: { username, password }
      };
    }
    return {};
  }, []);

  const value = useMemo(() => ({
    isAuthenticated, admin, loading, login, logout, getAuthHeader
  }), [isAuthenticated, admin, loading, login, logout, getAuthHeader]);

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
